import type { ReportLink } from './copilot.types';

export type ReportAnswerSegment =
  | { kind: 'text', text: string }
  | { kind: 'bold', text: string }
  | { kind: 'link', text: string, link: ReportLink };

function linkNeedles(link: ReportLink): string[] {
  return [...new Set(
    [...(link.phrases ?? []), link.label]
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
      .map((p) => p.trim()),
  )].sort((a, b) => b.length - a.length);
}

function indexOfPhrase(haystack: string, phrase: string, from: number): number {
  const lower = haystack.toLowerCase();
  return lower.indexOf(phrase.toLowerCase(), from);
}

function phrasesAppear(answer: string, link: ReportLink): boolean {
  return linkNeedles(link).some((p) => indexOfPhrase(answer, p, 0) >= 0);
}

/**
 * If the model omitted report phrases, append them so Copilot can turn them
 * into inline pills (never a leftover chip row).
 */
export function ensureReportPhrasesInAnswer(
  answer: string,
  links: ReportLink[] | undefined,
): string {
  const text = (answer ?? '').trim();
  if (!links?.length) {
    return text;
  }
  const missing = links
    .filter((l) => !phrasesAppear(text, l))
    .map((l) => (l.phrases?.[0] || l.label || '').trim())
    .filter(Boolean);
  if (!missing.length) {
    return text;
  }
  const clause = missing.join(' and ');
  if (!text) {
    return `${clause}.`;
  }
  const trimmed = text.replace(/[.!?]+\s*$/, '');
  return `${trimmed} — ${clause}.`;
}

export function explodeInlineBold(text: string): ReportAnswerSegment[] {
  if (!text) {
    return [];
  }
  const segments: ReportAnswerSegment[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    if (match.index > last) {
      segments.push({ kind: 'text', text: text.slice(last, match.index) });
    }
    segments.push({ kind: 'bold', text: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ kind: 'text', text: text.slice(last) });
  }
  return segments.length ? segments : [{ kind: 'text', text }];
}

/**
 * Split assistant prose so known report phrases become clickable pills.
 * Longest phrase wins; first occurrence of each link only. Missing phrases
 * are appended. `**bold**` becomes a bold segment.
 */
export function splitReportLinkPhrases(
  answer: string,
  links: ReportLink[] | undefined,
): ReportAnswerSegment[] {
  const text = ensureReportPhrasesInAnswer(answer, links);
  if (!text) {
    return [];
  }
  if (!links?.length) {
    return explodeInlineBold(text);
  }
  const needles: Array<{ phrase: string, link: ReportLink }> = [];
  for (const link of links) {
    for (const phrase of linkNeedles(link)) {
      needles.push({ phrase, link });
    }
  }
  const used = new Set<string>();
  const segments: ReportAnswerSegment[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    let best: { index: number, phrase: string, link: ReportLink, matched: string } | null =
      null;
    for (const needle of needles) {
      if (used.has(needle.link.id)) continue;
      const index = indexOfPhrase(text, needle.phrase, cursor);
      if (index < 0) continue;
      const matched = text.slice(index, index + needle.phrase.length);
      if (
        !best ||
        index < best.index ||
        (index === best.index && needle.phrase.length > best.phrase.length)
      ) {
        best = { index, phrase: needle.phrase, link: needle.link, matched };
      }
    }
    if (!best) {
      segments.push(...explodeInlineBold(text.slice(cursor)));
      break;
    }
    if (best.index > cursor) {
      segments.push(...explodeInlineBold(text.slice(cursor, best.index)));
    }
    segments.push({ kind: 'link', text: best.matched, link: best.link });
    used.add(best.link.id);
    cursor = best.index + best.phrase.length;
  }
  return segments.length ? segments : explodeInlineBold(text);
}
